// Core domain types for dig
// Private-first, local-first, exportable self-model revision logs.

export type Layer = 
  | 'surface'      // immediate triggers, news, bodily sensations
  | 'weather'      // current emotional / cognitive weather
  | 'patterns'     // recurring loops, stories I tell myself
  | 'values'       // what feels threatened, sacred, worth protecting
  | 'questions'    // the genuinely unanswerable or too-big questions
  | 'edges';       // where agency feels absent or strangely possible

export const LAYERS: { key: Layer; label: string; depth: string }[] = [
  { key: 'surface', label: 'Surface', depth: 'What just landed' },
  { key: 'weather', label: 'Weather', depth: 'How it is right now' },
  { key: 'patterns', label: 'Patterns', depth: 'What keeps returning' },
  { key: 'values', label: 'Values & Losses', depth: 'What is at stake' },
  { key: 'questions', label: 'Unanswerable', depth: 'Questions without resolution' },
  { key: 'edges', label: 'Agency Edges', depth: 'Where control feels thin' },
];

export interface Presence {
  workingMemoryLoad: number; // 1-5
  perceivedControl: number;  // 1-5, "right now, over the things that matter most here"
  contextSwitches: number;   // user-reported in this sitting
  resumedAfterHours?: number;
  note?: string;
}

export interface Artifact {
  id: string;
  layer: Layer;
  text: string;
  createdAt: string; // ISO
  // Optional user tags or observations
  tags?: string[];
  // When the user explicitly "sat with" this find
  brushedSeconds?: number;
}

export interface SelfModel {
  // The living, revisable statements. This + history = the shareable clarity.
  statements: string[]; // 2-6 short, honest lines the person maintains
  openQuestions: string[];
  updatedAt: string;
}

export interface Revision {
  id: string;
  number: number;
  createdAt: string;
  // Snapshot of the self-model at this moment
  model: SelfModel;
  // What changed in plain language (human or lightly assisted)
  narrative: string;
  // Ethical presence metadata — transparent and editable
  presence: Presence;
  // Which artifacts were considered "live" for this revision
  artifactIds: string[];
  // Optional short title the person gives it
  title?: string;
}

export interface DigState {
  id: string;           // the private key the user holds (dig_...)
  createdAt: string;
  lastActiveAt: string;
  title?: string;       // optional gentle name like "Winter 2026" or "After the report"
  
  artifacts: Artifact[];
  model: SelfModel;
  revisions: Revision[];
  
  // Ephemeral session presence (not persisted until revision)
  currentPresence: Presence;
  
  // For resumption warmth
  lastResumedAt?: string;
}

// The unit that gets exported / shared.
// A revision log is a deliberate, chosen slice of understanding.
export interface ClarityExport {
  version: 1;
  exportedAt: string;
  digTitle?: string;
  selectedRevisions: Revision[];
  selectedArtifactIds: string[];
  currentModel: SelfModel;
  // The presence notes are included only for the revisions chosen
  // Explicit consent flag for the person sharing
  shareNote?: string;
  // Human-written context the sharer adds
  contextForReader?: string;
}
